// Highgrove onboarding analytics — pure aggregation over HGR-tagged units and
// their `onboarding` checklist inspections. Powers the admin Highgrove tab:
// per-unit note counts (sort), status buckets (no inspection / partial /
// complete), and a per-step funnel of how far units have progressed.
import { unitHasTag } from './dataQueries';
import { checklistSteps, stepAddressed, stepNotes } from './inspectionProgress';
import { HGR_ONBOARD_PROPERTY_IDS } from '../data/highgroveProperties';

const HGR_TAG = 'HGR';
const ONBOARDING_TYPE = 'onboarding';

// Normalize any timestamp shape to epoch ms: Firestore Timestamp (createdAt,
// has .toMillis()/.seconds), ISO string (completedAt), Date, or epoch number.
// Returns null for anything unparseable — callers must treat that as "no time".
function toMillis(v) {
  if (v == null) return null;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (typeof v.seconds === 'number') return v.seconds * 1000 + (v.nanoseconds || 0) / 1e6;
  const t = typeof v === 'number' ? v : new Date(v).getTime();
  return Number.isNaN(t) ? null : t;
}

// Wall-clock time one onboard took: completedAt − createdAt. Only completed
// onboards have a real end, so in-progress ones return null (no fake duration).
// Negative deltas (clock skew / bad data) are dropped rather than shown.
export function onboardDurationMs(inspection) {
  if (inspection?.status !== 'complete') return null;
  const start = toMillis(inspection?.createdAt);
  const end = toMillis(inspection?.completedAt);
  if (start == null || end == null) return null;
  const d = end - start;
  return d >= 0 ? d : null;
}

// Human-readable duration: "Xd Yh" for days, "Xh Ym" for hours, "Xm" under an
// hour, "Xs" under a minute. null → "—" so unmeasured onboards read as absent.
export function formatOnboardDuration(ms) {
  if (ms == null) return '—';
  const secs = Math.round(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ${mins % 60}m`;
  const days = Math.floor(hrs / 24);
  return `${days}d ${hrs % 24}h`;
}

// Group cohort units per property, bucket properties by their unit count
// (1 / 2 / 3; properties with 4+ units are skipped), and report the AVERAGE time to complete a whole property in
// each bucket. A property's time = sum of its units' onboard times; only
// properties where EVERY unit is onboarded count (a half-done property has no
// real time-to-complete). `avgMs` = mean over complete properties; `null` if
// none. `completeProperties` vs `propertyCount` shows how many back the average.
export function groupByPropertySize(rows) {
  const byProperty = new Map();
  for (const r of (rows || [])) {
    if (!r.onboarding) continue; // cohort only — matches the progress stats
    const p = byProperty.get(r.propertyId) || { unitCount: 0, measuredUnits: 0, totalMs: 0 };
    p.unitCount += 1;
    if (r.durationMs != null) { p.measuredUnits += 1; p.totalMs += r.durationMs; }
    byProperty.set(r.propertyId, p);
  }
  const buckets = ['1', '2', '3'].map((size) => ({
    size, propertyCount: 0, completeProperties: 0, completeMs: 0, avgMs: null,
  }));
  for (const p of byProperty.values()) {
    if (p.unitCount < 1 || p.unitCount > 3) continue; // no 4+ bucket — skip
    const b = buckets[p.unitCount - 1];
    b.propertyCount += 1;
    if (p.measuredUnits === p.unitCount) { // whole property onboarded
      b.completeProperties += 1;
      b.completeMs += p.totalMs;
    }
  }
  for (const b of buckets) b.avgMs = b.completeProperties ? b.completeMs / b.completeProperties : null;
  const completeProperties = buckets.reduce((s, b) => s + b.completeProperties, 0);
  const completeMs = buckets.reduce((s, b) => s + b.completeMs, 0);
  return { buckets, completeProperties, avgMs: completeProperties ? completeMs / completeProperties : null };
}

// Count free-text notes captured in one onboarding inspection: the top-level
// Notes field (1 if filled) plus each checklist step that carries a note.
export function inspectionNoteCount(inspection) {
  const items = inspection?.items || {};
  const stepNotes = Object.values(items).filter((e) => e?.note?.trim()).length;
  const topNote = inspection?.notes?.trim() ? 1 : 0;
  return topNote + stepNotes;
}

// Ordered note "slides" for one inspection: the top-level Notes field (if any)
// first, then every checklist step carrying a note and/or photos. Same slide
// shape stepNotes() produces, so the notes slideshow pages through them uniformly.
export function inspectionNoteSlides(inspection, def) {
  const slides = [];
  if (inspection?.notes?.trim()) {
    slides.push({
      id: `${inspection.id || 'insp'}__notes`,
      sectionTitle: 'Inspection',
      title: 'Notes',
      note: inspection.notes,
      photos: inspection.photos || [],
    });
  }
  slides.push(...stepNotes(def, inspection?.items || {}));
  return slides;
}

// Set of step ids a unit has addressed (done/skipped) across ALL its onboarding
// inspections — a step counts as reached if any of the unit's inspections hit it.
function addressedStepIds(inspections, steps) {
  const ids = new Set();
  for (const insp of inspections) {
    const items = insp.items || {};
    for (const step of steps) {
      if (stepAddressed(items[step.id])) ids.add(step.id);
    }
  }
  return ids;
}

/**
 * Full onboarding analytics for HGR units.
 * @param {Array} units - all unit docs (filtered here to HGR-tagged)
 * @param {Array} inspections - all inspection docs (filtered here to onboarding)
 * @param {Object} def - the onboarding checklist definition (sections/steps)
 * @returns {{ rows, buckets, stepFunnel, total, sizeBuckets }}
 *   rows        — per HGR unit, sorted by noteCount desc: { unitId, propertyId, durationMs, address, property, onboarding, status, done, total, noteCount, noteSlides }
 *   buckets     — { none, partial, complete } counts over the onboarding cohort only
 *   stepFunnel  — [{ id, title, count }] cohort units that have addressed each step
 *   total       — onboarding-cohort unit count (the progress max); rows.length is all HGR units
 *   sizeBuckets — { buckets: [{ size, propertyCount, completeProperties, completeMs, avgMs }], completeProperties, avgMs } avg time to complete a whole property, grouped by property unit-count
 */
export function analyzeHgrOnboarding(units, inspections, def) {
  const steps = checklistSteps(def);
  const hgrUnits = (units || []).filter((u) => unitHasTag(u.tags, HGR_TAG));
  const onboarding = (inspections || []).filter((i) => i.type === ONBOARDING_TYPE);

  const rows = hgrUnits
    .map((unit) => {
      const unitInsps = onboarding.filter((i) => String(i.unitId) === String(unit.id));
      const durations = unitInsps.map(onboardDurationMs).filter((d) => d != null);
      const durationMs = durations.length ? durations.reduce((a, b) => a + b, 0) : null;
      const noteCount = unitInsps.reduce((sum, i) => sum + inspectionNoteCount(i), 0);
      const noteSlides = unitInsps.flatMap((i) => inspectionNoteSlides(i, def));
      const addressed = addressedStepIds(unitInsps, steps);
      const status = unitInsps.length === 0
        ? 'none'
        : unitInsps.some((i) => i.status === 'complete')
          ? 'complete'
          : 'partial';
      return {
        unitId: unit.id,
        propertyId: String(unit.propertyId),
        durationMs, // total completed-onboard time for this unit, null if none measured
        address: unit.address || '',
        property: unit.propertyName || '',
        // Cohort = properties we actually onboard. Every HGR unit is still
        // listed; only cohort units feed the progress buckets/funnel/max below.
        onboarding: HGR_ONBOARD_PROPERTY_IDS.has(String(unit.propertyId)),
        status,
        done: addressed.size,
        total: steps.length,
        noteCount,
        noteSlides,
        addressed,
      };
    })
    .sort((a, b) => b.noteCount - a.noteCount);

  const cohort = rows.filter((r) => r.onboarding);

  const buckets = { none: 0, partial: 0, complete: 0 };
  for (const r of cohort) buckets[r.status]++;

  const stepFunnel = steps.map((step) => ({
    id: step.id,
    title: step.title,
    count: cohort.filter((r) => r.addressed.has(step.id)).length,
  }));

  return { rows, buckets, stepFunnel, total: cohort.length, sizeBuckets: groupByPropertySize(rows) };
}
